// CoverageField primitive — a continuous, thresholdable coverage field (Set11 Phase 3).
// Fixes the foam confetti: `scatter` is a lattice of discrete stamps, structurally incapable of
// connected/streaky regions; a coverage field is a band-limited noise field whose soft threshold
// yields connected, ragged, dilatable patches with sub-pixel-correct edges.
//
// NOTE (repo hazard): no backticks and no GLSL-ES-300 reserved identifiers (flat/smooth/...)
// inside the template literal.
export const COVERAGE_FIELD_GLSL = `
// The coverage field VALUE (0..1, after the soft threshold).
//
// p in world meters.  scale = the field's base feature size (m); octaves/mode pass through to
// fbmVal (mode 3 = turbulence -> ragged, patchy); anisotropy/angleDeg stretch the field ALONG
// the angle (streaks); threshold (0..1) = the coverage fraction knob (higher = less coverage);
// edgeSoftness (0..0.5) = the transition width in FIELD-VALUE units (widened by the pixel
// footprint in field-value units so the boundary never becomes a hard step at coarse zoom);
// warpScale/warpAmount = the domainWarpP args (ragged, non-blobby boundaries).
float coverageFieldVal(vec2 p, float scale, int octaves, int mode, float anisotropy, float angleDeg, float threshold, float edgeSoftness, float warpScale, float warpAmount) {
  float a = angleDeg * 0.01745329252;
  vec2 dir = vec2(cos(a), sin(a));
  vec2 perp = vec2(-dir.y, dir.x);
  // Anisotropic domain: compress ALONG dir so features stretch in that direction (streaks).
  vec2 q = vec2(dot(p, dir) / max(anisotropy, 1.0), dot(p, perp));
  q = domainWarpP(q, warpScale, 3, warpAmount);
  float v = fbmVal(q, scale, octaves, 2.0, 0.5, mode);
  // Remap to [0,1] like the fbm layerBody does for the mode's raw output.
  if (mode == 0 || mode == 2) v = v * 0.5 + 0.5;
  // Soft threshold.  edgeSoftness is widened by the PIXEL footprint expressed in field-value
  // units (px / scale — the field value changes by ~1 over one feature size) so the boundary
  // never becomes a hard step at coarse zoom (the Set11 plan Section 7 rule).
  float px = matPixelSize();
  float pxInField = clamp(px / max(scale, 1e-5), 0.0, 0.5);
  float soft = max(edgeSoftness, pxInField);
  // Spec-clean edges (edge0 < edge1 ALWAYS — inverted smoothstep edges are undefined in GLSL
  // ES 300; this repo has been bitten by it repeatedly).
  return smoothstep(threshold - soft, threshold + soft, v);
}
`;
